import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Send } from 'lucide-react';
import { cn } from '../../utils/cn';
import SuccessAnimation from '../common/SuccessAnimation';

const helpOptions = [
  'Website Design & Development',
  'Website Maintenance & Support',
  'Logo Design & Branding',
  'App Development',
  'Digital Marketing Strategy',
  'Digital Transformation',
  'Something else',
];

const ContactForm = () => {
  const [formState, setFormState] = useState({
    businessName: '',
    fullName: '',
    email: '',
    phone: '',
    topic: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://formspree.io/f/xovveglb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formState)
      });

      const data = await response.json();
      
      if (response.ok) {
        setIsSuccess(true);
        setFormState({
          businessName: '',
          fullName: '',
          email: '',
          phone: '',
          topic: '',
          message: ''
        });
      } else {
        throw new Error(data.error || 'Failed to submit form');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return <SuccessAnimation />;
  }

  return (
    <div className={cn(
      "relative p-8 rounded-3xl w-full",
      "bg-[#111111]",
      "backdrop-blur-sm",
      "border border-primary-500/20",
      "hover:border-primary-500/40 transition-all duration-500",
      "shadow-lg shadow-primary-900/20",
      "hover:shadow-xl hover:shadow-primary-900/30",
      "group"
    )}>
      {/* Enhanced background glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-3xl" />
      
      <h2 className="text-3xl font-bold text-white mb-8">Send Us A Message</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Business Name */}
          <input
            type="text"
            name="businessName"
            placeholder="Business Name*"
            value={formState.businessName}
            onChange={handleChange}
            className={cn(
              "w-full px-4 py-3 rounded-lg",
              "bg-dark/30 backdrop-blur-sm",
              "border border-primary-500/20",
              "text-white placeholder-gray-400",
              "focus:border-primary-500/40 focus:ring-2 focus:ring-primary-500/20",
              "hover:border-primary-500/30",
              "transition-all duration-300"
            )}
            required
          />

          {/* Full Name */}
          <input
            type="text"
            name="fullName"
            placeholder="Full Name*"
            value={formState.fullName}
            onChange={handleChange}
            className={cn(
              "w-full px-4 py-3 rounded-lg",
              "bg-dark/30 backdrop-blur-sm",
              "border border-primary-600/20",
              "text-white placeholder-gray-400",
              "focus:border-primary-600/40 focus:ring-2 focus:ring-primary-600/20",
              "transition-all duration-300"
            )}
            required
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Email */}
          <input
            type="email"
            name="email"
            placeholder="Email Address*"
            value={formState.email}
            onChange={handleChange}
            className={cn(
              "w-full px-4 py-3 rounded-lg",
              "bg-dark/30 backdrop-blur-sm",
              "border border-primary-600/20",
              "text-white placeholder-gray-400",
              "focus:border-primary-600/40 focus:ring-2 focus:ring-primary-600/20",
              "transition-all duration-300"
            )}
            required
          />

          {/* Phone */}
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number*"
            value={formState.phone}
            onChange={handleChange}
            className={cn(
              "w-full px-4 py-3 rounded-lg",
              "bg-dark/30 backdrop-blur-sm",
              "border border-primary-600/20",
              "text-white placeholder-gray-400",
              "focus:border-primary-600/40 focus:ring-2 focus:ring-primary-600/20",
              "transition-all duration-300"
            )}
            required
          />
        </div>

        {/* Topic */}
        <div className="relative">
          <select
            name="topic"
            aria-label="What can we help you with?"
            value={formState.topic}
            onChange={handleChange}
            className={cn(
              "w-full appearance-none px-4 py-3 pr-10 rounded-lg",
              "bg-dark/30 backdrop-blur-sm",
              "border border-primary-600/20",
              formState.topic ? "text-white" : "text-gray-400",
              "focus:border-primary-600/40 focus:ring-2 focus:ring-primary-600/20",
              "transition-all duration-300"
            )}
            required
          >
            <option value="" disabled>
              What can we help you with?*
            </option>
            {helpOptions.map((option) => (
              <option key={option} value={option} className="text-black">
                {option}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        </div>

        {/* Message */}
        <div>
          <textarea
            name="message"
            placeholder="Tell us about what you are working on...*"
            value={formState.message}
            onChange={handleChange}
            rows={6}
            className={cn(
              "w-full px-4 py-3 rounded-lg",
              "bg-dark/30 backdrop-blur-sm",
              "border border-primary-600/20",
              "text-white placeholder-gray-400",
              "focus:border-primary-600/40 focus:ring-2 focus:ring-primary-600/20",
              "transition-all duration-300"
            )}
            required
          />
        </div>

        <motion.button
          type="submit"
          className={cn(
            "w-full py-4 rounded-full text-lg font-medium",
            "bg-primary-500 text-white",
            "hover:bg-primary-600",
            "transition-all duration-300",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "flex items-center justify-center gap-2",
            "shadow-lg shadow-primary-900/20",
            "hover:shadow-xl hover:shadow-primary-900/30",
            "relative overflow-hidden group"
          )}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
          disabled={isSubmitting}
        >
          {/* Button glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-400/0 via-primary-400/30 to-primary-400/0 -z-10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <span className="relative z-10">Send Message</span>
              <Send className="w-5 h-5 relative z-10 transform transition-transform group-hover:translate-x-1 group-hover:rotate-12" />
            </>
          )}
        </motion.button>
      </form>
      
      {/* Corner indicators */}
      <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-primary-400/30 opacity-0 group-hover:opacity-100 transition-all duration-300" />
      <div className="absolute bottom-4 left-4 w-2 h-2 rounded-full bg-primary-400/30 opacity-0 group-hover:opacity-100 transition-all duration-300" />
    </div>
  );
};

export default ContactForm;